<template>
  <div class="stall-management-mobile">
    <!-- 顶部标题 -->
    <div class="page-top-bar">
      <div class="top-left" @click="goBack">
        <span class="back-icon">←</span>
      </div>
      <div class="top-title">{{ $t('wholesaler.StallManagement.line8_text') }}</div>
      <div class="top-right" @click="goToAddPage">
        <span class="add-icon">＋</span>
      </div>
    </div>

    <!-- 搜索栏 -->
    <div class="search-section">
      <div class="search-bar">
        <el-input
          v-model="searchKeyword"
          :placeholder="$t('wholesaler.StallManagement.line19_placeholder')"
          prefix-icon="el-icon-search"
          clearable
          @clear="loadStalls"
          @keyup.enter.native="loadStalls"
        />
      </div>
      <div class="filter-row">
        <el-input
          v-model="searchPhone"
          :placeholder="$t('wholesaler.StallManagement.line29_placeholder')"
          size="small"
          clearable
          @clear="loadStalls"
          @keyup.enter.native="loadStalls"
        />
        <el-input
          v-model="searchContact"
          :placeholder="$t('wholesaler.StallManagement.line37_placeholder')"
          size="small"
          clearable
          @clear="loadStalls"
          @keyup.enter.native="loadStalls"
        />
        <el-select
          v-model="searchRegion"
          :placeholder="$t('wholesaler.StallManagement.line45_placeholder')"
          size="small"
          clearable
          @change="loadStalls"
        >
          <el-option
            v-for="r in regionList"
            :key="r.id"
            :label="r.regionName"
            :value="r.regionName"
          />
        </el-select>
      </div>
      <div class="filter-buttons">
        <el-button size="mini" type="primary" @click="loadStalls">{{ $t('common.search') }}</el-button>
        <el-button size="mini" @click="resetFilter">{{ $t('wholesaler.StallManagement.line60_text') }}</el-button>
      </div>
    </div>

    <!-- 统计 -->
    <div class="stats-bar">
      <span>{{ $t('common.total') }} <strong>{{ total }}</strong> {{ $t('wholesaler.StallManagement.line66_text') }}</span>
    </div>

    <!-- 档口列表 -->
    <div class="stall-list" v-loading="loading">
      <div v-if="!loading && stallList.length === 0" class="empty-state">
        <div class="empty-icon">🏪</div>
        <p>{{ $t('wholesaler.StallManagement.line73_text') }}</p>
        <el-button type="primary" size="small" @click="showAddDialog">{{ $t('wholesaler.StallManagement.line74_text') }}</el-button>
      </div>

      <div v-else>
        <div v-for="stall in filteredStalls" :key="stall.id" class="stall-card">
          <div class="stall-card-header">
            <div class="stall-avatar">{{ (stall.customerName || stall.customerName || '?').charAt(0) }}</div>
            <div class="stall-info">
              <div class="stall-name">{{ stall.customerName || stall.customerName }}</div>
              <div class="stall-code">{{ stall.customerCode }}</div>
            </div>
            <div class="stall-actions">
              <el-button v-if="canEditStall(stall)" size="mini" type="primary" plain @click="showEditDialog(stall)">{{ $t('wholesaler.StallManagement.edit') }}</el-button>
              <el-button v-if="canEditStall(stall)" size="mini" type="warning" plain @click="showAccountDialog(stall)">{{ $t('wholesaler.StallManagement.account') }}</el-button>
              <el-button size="mini" type="danger" plain @click="removeStall(stall)">{{ $t('wholesaler.StallManagement.line85_text') }}</el-button>
            </div>
          </div>
          <div class="stall-card-body">
            <div class="stall-detail-row" v-if="stall.contactPerson">
              <span class="detail-label">{{ $t('wholesaler.StallManagement.line89_text') }}</span>
              <span class="detail-value">{{ stall.contactPerson }}</span>
            </div>
            <div class="stall-detail-row" v-if="stall.phone">
              <span class="detail-label">{{ $t('wholesaler.StallManagement.line93_text') }}</span>
              <span class="detail-value">{{ stall.phone }}</span>
            </div>
            <div class="stall-detail-row" v-if="stall.regionName">
              <span class="detail-label">{{ $t('wholesaler.StallManagement.line97_text') }}</span>
              <span class="detail-value">{{ stall.regionName }}</span>
            </div>
            <div class="stall-detail-row" v-if="stall.createdAt">
              <span class="detail-label">{{ $t('wholesaler.StallManagement.line101_text') }}</span>
              <span class="detail-value">{{ formatDate(stall.createdAt) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 分页 -->
    <div class="pagination-wrapper" v-if="total > pageSize">
      <el-pagination
        @current-change="handlePageChange"
        :current-page="currentPage"
        :page-size="pageSize"
        layout="prev, pager, next, total"
        :total="total">
      </el-pagination>
    </div>

    <!-- 编辑档口弹窗 -->
    <el-dialog
      :title="$t('wholesaler.StallManagement.editStall')"
      :visible.sync="editDialogVisible"
      :fullscreen="true"
      :append-to-body="true"
      custom-class="edit-stall-dialog"
    >
      <div class="edit-stall-content" v-if="editingStall">
        <div class="form-group">
          <label>{{ $t('wholesaler.StallAdd.stallName') }}</label>
          <el-input v-model="editingStall.customerName" :placeholder="$t('wholesaler.StallAdd.line29_placeholder')" />
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.StallAdd.contactPerson') }}</label>
          <el-input v-model="editingStall.contactPerson" :placeholder="$t('wholesaler.StallAdd.line44_placeholder')" />
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.StallAdd.contactPhone') }}</label>
          <el-input v-model="editingStall.phone" :placeholder="$t('wholesaler.StallAdd.line59_placeholder')" />
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.StallAdd.region') }}</label>
          <el-select v-model="editingStall.regionId" :placeholder="$t('wholesaler.StallAdd.line77_text')" style="width: 100%">
            <el-option
              v-for="r in regionList"
              :key="r.id"
              :label="r.regionName"
              :value="r.id"
            />
          </el-select>
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.StallAdd.remark') }}</label>
          <el-input v-model="editingStall.remark" type="textarea" :rows="3" :placeholder="$t('wholesaler.StallAdd.line134_placeholder')" />
        </div>
        <div class="dialog-footer">
          <el-button @click="editDialogVisible = false">{{ $t('wholesaler.StallManagement.cancel') }}</el-button>
          <el-button type="primary" @click="submitEdit" :loading="editSubmitting">{{ $t('wholesaler.StallManagement.confirm') }}</el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 账号管理弹窗 -->
    <el-dialog
      :title="$t('wholesaler.StallManagement.account')"
      :visible.sync="accountDialogVisible"
      :fullscreen="true"
      :append-to-body="true"
      custom-class="account-stall-dialog"
    >
      <div class="account-stall-content" v-if="accountStall" v-loading="accountLoading">
        <div class="stall-info-header">
          <h3>{{ accountStall.customerName }}</h3>
          <p>{{ accountStall.contactPerson }} · {{ accountStall.phone }}</p>
        </div>
        <el-form :model="accountForm" label-width="100px">
          <div class="form-group">
            <label>{{ $t('wholesaler.StallManagement.username') }}</label>
            <el-input v-model="accountForm.username" :placeholder="$t('wholesaler.StallManagement.usernamePlaceholder')" />
          </div>
          <div class="form-group">
            <label>{{ $t('wholesaler.StallManagement.password') }}</label>
            <el-input v-model="accountForm.password" type="password" :placeholder="$t('wholesaler.StallManagement.passwordPlaceholder')" show-password />
          </div>
          <div class="form-group">
            <label>{{ $t('wholesaler.StallManagement.confirmPassword') }}</label>
            <el-input v-model="accountForm.confirmPassword" type="password" :placeholder="$t('wholesaler.StallManagement.confirmPasswordPlaceholder')" show-password />
          </div>
        </el-form>
        <div class="dialog-footer">
          <el-button @click="accountDialogVisible = false">{{ $t('wholesaler.StallManagement.cancel') }}</el-button>
          <el-button type="primary" @click="submitAccount" :loading="accountSubmitting">{{ $t('wholesaler.StallManagement.confirm') }}</el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 添加档口弹窗 -->
    <el-dialog
      :title="$t('wholesaler.StallManagement.line122_title')"
      :visible.sync="addDialogVisible"
      :fullscreen="true"
      :append-to-body="true"
      custom-class="add-stall-dialog"
    >
      <div class="add-stall-content">
        <div class="search-available">
          <el-input
            v-model="searchAvailableKeyword"
            :placeholder="$t('wholesaler.StallManagement.line132_placeholder')"
            prefix-icon="el-icon-search"
            clearable
            @clear="searchAvailableStalls"
            @keyup.enter.native="searchAvailableStalls"
          />
        </div>

        <div class="available-list" v-loading="searching">
          <div v-if="availableStalls.length === 0 && !searching" class="empty-tip">
            <div class="empty-icon">🔍</div>
            <p>{{ $t('wholesaler.StallManagement.line143_text') }}</p>
          </div>

          <div
            v-for="stall in availableStalls"
            :key="stall.id"
            class="available-stall-item"
          >
            <div class="available-stall-left">
              <div class="stall-avatar small">{{ (stall.customerName || stall.customerName || '?').charAt(0) }}</div>
              <div class="stall-text">
                <div class="stall-name">{{ stall.customerName || stall.customerName }}</div>
                <div class="stall-meta">
                  <span v-if="stall.contactPerson">{{ stall.contactPerson }}</span>
                  <span v-if="stall.phone">· {{ stall.phone }}</span>
                </div>
                <div class="stall-region" v-if="stall.regionName">{{ stall.regionName }}</div>
              </div>
            </div>
            <el-button type="primary" size="mini" @click="addStall(stall)">{{ $t('wholesaler.StallManagement.line162_text') }}</el-button>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'StallManagement',
  data() {
    return {
      stallList: [],
      loading: false,
      searchKeyword: '',
      searchPhone: '',
      searchContact: '',
      searchRegion: '',
      currentPage: 1,
      pageSize: 20,
      total: 0,
      regionList: [],
      addDialogVisible: false,
      availableStalls: [],
      searching: false,
      searchAvailableKeyword: '',
      editDialogVisible: false,
      editingStall: null,
      editSubmitting: false,
      accountDialogVisible: false,
      accountStall: null,
      accountForm: {
        username: '',
        password: '',
        confirmPassword: ''
      },
      accountLoading: false,
      accountSubmitting: false
    }
  },
  computed: {
    filteredStalls() {
      return this.stallList
    }
  },
  mounted() {
    this.loadStalls()
    this.loadRegions()
  },
  methods: {
    goBack() {
      this.$router.back()
    },

    goToAddPage() {
      this.$router.push('/wholesaler/stalls/add')
    },

    async loadStalls() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          size: this.pageSize
        }
        if (this.searchKeyword && this.searchKeyword.trim()) {
          params.keyword = this.searchKeyword.trim()
        }
        if (this.searchPhone && this.searchPhone.trim()) {
          params.phone = this.searchPhone.trim()
        }
        if (this.searchContact && this.searchContact.trim()) {
          params.contactPerson = this.searchContact.trim()
        }
        if (this.searchRegion && this.searchRegion.trim()) {
          params.region = this.searchRegion.trim()
        }
        const response = await api.get('/wholesaler/stalls', { params })
        console.log('📋 API 响应:', response)
        
        // 兼容两种格式：
        // 1. 拦截器已解包：{items: [...], total: ...}
        // 2. 未解包：{success: true, data: {items: [...], total: ...}}
        let data = response
        if (response?.success && response?.data) {
          data = response.data
        } else if (response?.data) {
          data = response.data
        }
        
        console.log('📋 解析后的数据:', data)
        this.stallList = (data?.items || []).map(stall => ({
          ...stall,
          id: stall.stallId || stall.id
        }))
        this.total = data?.total || 0
      } catch (error) {
        this.$message.error('加载档口列表失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    async loadRegions() {
      try {
        const response = await api.get('/wholesaler/stalls/regions')
        this.regionList = Array.isArray(response) ? response : (response?.data || [])
      } catch (error) {
        console.error('加载区域列表失败', error)
      }
    },

    resetFilter() {
      this.searchKeyword = ''
      this.searchPhone = ''
      this.searchContact = ''
      this.searchRegion = ''
      this.currentPage = 1
      this.loadStalls()
    },

    handlePageChange(page) {
      this.currentPage = page
      this.loadStalls()
    },

    showAddDialog() {
      this.addDialogVisible = true
      this.searchAvailableKeyword = ''
      this.availableStalls = []
      this.searchAvailableStalls()
    },

    async searchAvailableStalls() {
      this.searching = true
      try {
        const params = {}
        if (this.searchAvailableKeyword) {
          params.keyword = this.searchAvailableKeyword
        }
        const response = await api.get('/wholesaler/stalls/search', { params })
        this.availableStalls = Array.isArray(response) ? response : (response?.data || [])
      } catch (error) {
        this.$message.error('搜索档口失败')
        console.error(error)
      } finally {
        this.searching = false
      }
    },

    async addStall(stall) {
      try {
        await api.post('/wholesaler/stalls', { stallId: stall.id })
        this.$message.success('添加成功')
        this.addDialogVisible = false
        this.loadStalls()
      } catch (error) {
        this.$message.error(error.response?.data?.message || '添加失败')
        console.error(error)
      }
    },

    async removeStall(stall) {
      try {
        await this.$confirm(this.$t('wholesaler.StallManagement.confirmUnlink', { name: stall.customerName || stall.customerName }), this.$t('wholesaler.StallManagement.unlinkTitle'), {
          confirmButtonText: this.$t('wholesaler.StallManagement.confirm'),
          cancelButtonText: this.$t('wholesaler.StallManagement.cancel'),
          type: 'warning'
        })
        await api.delete(`/wholesaler/stalls/${stall.stallId}`)
        this.$message.success(this.$t('wholesaler.StallManagement.unlinkSuccess'))
        this.loadStalls()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error(this.$t('wholesaler.StallManagement.removeFailed'))
          console.error(error)
        }
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    },

    canEditStall(stall) {
      // 判断是否是自己创建的档口
      const wholesalerId = localStorage.getItem('stored_wholesaler_id') || localStorage.getItem('identityId') || localStorage.getItem('wholesalerId')
      return wholesalerId && stall.createdBy && String(stall.createdBy) === String(wholesalerId)
    },

    showEditDialog(stall) {
      // 找到对应区域的ID
      let regionId = null
      if (stall.regionName) {
        const region = this.regionList.find(r => r.regionName === stall.regionName)
        if (region) regionId = region.id
      }
      this.editingStall = {
        id: stall.stallId,
        customerName: stall.customerName,
        contactPerson: stall.contactPerson,
        phone: stall.phone,
        regionId: regionId,
        remark: stall.remark || ''
      }
      this.editDialogVisible = true
    },

    async submitEdit() {
      if (!this.editingStall) return
      
      if (!this.editingStall.customerName || !this.editingStall.customerName.trim()) {
        this.$message.error(this.$t('wholesaler.StallAdd.pleaseEnterStallName'))
        return
      }
      if (!this.editingStall.contactPerson || !this.editingStall.contactPerson.trim()) {
        this.$message.error(this.$t('wholesaler.StallAdd.pleaseEnterContactPerson'))
        return
      }
      if (!this.editingStall.phone || !this.editingStall.phone.trim()) {
        this.$message.error(this.$t('wholesaler.StallAdd.pleaseEnterContactPhone'))
        return
      }

      this.editSubmitting = true
      try {
        await api.put(`/wholesaler/stalls/${this.editingStall.id}`, {
          customerName: this.editingStall.customerName,
          contactPerson: this.editingStall.contactPerson,
          phone: this.editingStall.phone,
          regionId: this.editingStall.regionId,
          remark: this.editingStall.remark
        })
        this.$message.success(this.$t('wholesaler.StallManagement.updateSuccess'))
        this.editDialogVisible = false
        this.loadStalls()
      } catch (error) {
        this.$message.error(error.response?.data?.message || this.$t('wholesaler.StallManagement.updateFailed'))
        console.error(error)
      } finally {
        this.editSubmitting = false
      }
    },

    async showAccountDialog(stall) {
      this.accountStall = stall
      this.accountForm = {
        username: '',
        password: '',
        confirmPassword: ''
      }
      this.accountDialogVisible = true
      this.accountLoading = true
      
      try {
        const response = await api.get(`/wholesaler/stalls/${stall.stallId}/user`)
        let data = response
        if (response?.success && response?.data) {
          data = response.data
        } else if (response?.data) {
          data = response.data
        }
        
        if (data) {
          this.accountForm.username = data.username || ''
        }
      } catch (error) {
        console.error('获取用户信息失败:', error)
      } finally {
        this.accountLoading = false
      }
    },

    async submitAccount() {
      if (!this.accountStall) return
      
      // 验证密码
      if (this.accountForm.password || this.accountForm.confirmPassword) {
        if (this.accountForm.password !== this.accountForm.confirmPassword) {
          this.$message.error(this.$t('wholesaler.StallManagement.passwordMismatch'))
          return
        }
        if (this.accountForm.password.length < 6) {
          this.$message.error(this.$t('wholesaler.StallManagement.passwordTooShort'))
          return
        }
      }
      
      this.accountSubmitting = true
      try {
        const payload = {
          username: this.accountForm.username,
          password: this.accountForm.password
        }
        
        await api.put(`/wholesaler/stalls/${this.accountStall.stallId}/user`, payload)
        this.$message.success(this.$t('wholesaler.StallManagement.updateSuccess'))
        this.accountDialogVisible = false
      } catch (error) {
        this.$message.error(error.response?.data?.message || this.$t('wholesaler.StallManagement.updateFailed'))
        console.error(error)
      } finally {
        this.accountSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.stall-management-mobile {
  min-height: 100vh;
  background: #f5f7fa;
  padding-bottom: 20px;
  padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px));
}

/* 顶部导航栏 */
.page-top-bar {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  height: calc(50px + env(safe-area-inset-top, 0px));
  padding-top: env(safe-area-inset-top, 0px);
  background: #fff;
  border-bottom: 1px solid #eee;
  padding-left: 16px;
  padding-right: 16px;
}

.top-left, .top-right {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}

.back-icon {
  font-size: 22px;
  color: #333;
}

.top-title {
  flex: 1;
  font-size: 17px;
  font-weight: 600;
  color: #333;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.add-icon {
  font-size: 22px;
  color: #409eff;
  font-weight: bold;
}

/* 搜索栏 */
.search-section {
  padding: 12px 16px 0;
}

.search-bar {
  background: #fff;
  border-radius: 8px;
  padding: 8px 12px;
}

/* 筛选 */
.filter-toggle {
  text-align: center;
  padding: 8px 0 4px;
  font-size: 12px;
  color: #409eff;
  cursor: pointer;
}

.filter-row {
  display: flex;
  gap: 8px;
  padding: 8px 0 0;
}

.filter-row .el-input,
.filter-row .el-select {
  flex: 1;
}

.filter-buttons {
  display: flex;
  gap: 8px;
  padding: 8px 0 4px;
  justify-content: center;
}

/* 统计栏 */
.stats-bar {
  padding: 10px 16px;
  font-size: 13px;
  color: #888;
}

.stats-bar strong {
  color: #409eff;
}

/* 档口列表 */
.stall-list {
  padding: 0 16px;
}

.stall-card {
  background: #fff;
  border-radius: 12px;
  margin-bottom: 12px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.stall-card-header {
  display: flex;
  align-items: center;
  padding: 14px 14px 10px;
  gap: 12px;
}

.stall-avatar {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
  flex-shrink: 0;
}

.stall-avatar.small {
  width: 38px;
  height: 38px;
  font-size: 15px;
  border-radius: 8px;
}

.stall-info {
  flex: 1;
  min-width: 0;
}

.stall-name {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stall-code {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

.stall-card-body {
  padding: 0 14px 14px;
}

.stall-detail-row {
  display: flex;
  align-items: center;
  padding: 5px 0;
  font-size: 13px;
}

.detail-label {
  color: #888;
  width: 80px;
  flex-shrink: 0;
}

.detail-value {
  color: #333;
  flex: 1;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.empty-state .empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.empty-state p {
  margin: 0 0 16px;
  font-size: 14px;
}

/* 添加弹窗 */
.add-stall-content {
  padding: 0;
}

.search-available {
  margin-bottom: 16px;
}

.available-list {
  max-height: calc(100vh - 180px);
  overflow-y: auto;
}

.available-stall-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  margin-bottom: 10px;
  background: #fafafa;
}

.available-stall-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.stall-text {
  flex: 1;
  min-width: 0;
}

.stall-text .stall-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stall-meta {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}

.stall-region {
  font-size: 11px;
  color: #aaa;
  margin-top: 2px;
}

.empty-tip {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.empty-tip .empty-icon {
  font-size: 36px;
  margin-bottom: 8px;
}

.empty-tip p {
  margin: 0;
  font-size: 14px;
}

/* 全屏弹窗样式覆盖 */
.add-stall-dialog {
  border-radius: 0 !important;
}
</style>
